import { Controller } from "@hotwired/stimulus"

// Polls IssuesController#refresh for a fresh render of the issue table (or its empty state), on a
// fixed interval: unlike live_chart_controller.js, there's no range picker here, just one
// cadence, since a status/count showing up a few seconds late is the whole ask, not a tunable
// time window. The current filters, sort, and page are never read off this element's own dataset:
// window.location.search already has all of it (see issues/index.html.erb), so a poll just
// re-requests the exact page the visitor is looking at and swaps in whatever comes back.
const POLL_INTERVAL_MS = 10000

export default class extends Controller {
  static values = { url: String }
  static targets = ["content"]

  connect() {
    this.timer = setInterval(() => this.refresh(), POLL_INTERVAL_MS)
    // Every status/assignee change in the table (_issue_status_cell.html.erb,
    // _issue_assignee_cell.html.erb) is a plain button_to form; left alone, submitting one is a
    // full Turbo Drive visit back to this same index page, which (being a fresh visit, not a
    // back/forward restoration) scrolls to the top every time, exactly the opposite of what
    // resolving or reassigning several issues in a row down the list wants. contentTarget, not
    // this.element: pagination's own links (shared/_pagination.html.erb) live inside this
    // controller's element too, and a plain link firing a "submit" listener would just never
    // match, but scoping to contentTarget keeps this listener's own job legible as "only the
    // table's own row actions." One listener survives every refresh() below despite it replacing
    // contentTarget's entire innerHTML: it's bound to contentTarget itself, not to any row inside
    // it, and submit still bubbles up to it from whatever's currently inside.
    this.contentTarget.addEventListener("submit", this.handleRowAction)
  }

  disconnect() {
    clearInterval(this.timer)
    this.contentTarget.removeEventListener("submit", this.handleRowAction)
  }

  // Arrow function assigned as a property, not a prototype method: addEventListener/
  // removeEventListener above need the exact same function reference both times, which a plain
  // method bound fresh via .bind() on each call would never give removeEventListener.
  handleRowAction = async (event) => {
    event.preventDefault()
    const form = event.target

    try {
      // The action's own redirect_back_or_to response (still there, unmodified, for a visitor
      // with JS disabled) is never read here: its only job for this listener is to persist the
      // change server-side, and refresh() below already re-fetches this exact list from scratch
      // once it has, the same "don't bother surfacing a dropped request" posture the poll already
      // takes below, not a new one invented just for this.
      await fetch(form.action, { method: "POST", body: new FormData(form) })
    } catch {
      // See above: nothing to show for a failed request here either; the list simply won't have
      // changed once refresh() below re-fetches it.
    }

    this.refresh()
  }

  // Same reasoning as live_chart_controller.js's own refresh: a poll the tab can't see is a query
  // spent on a table nobody's looking at, and a failed request (a deploy, a network blip) isn't
  // worth surfacing for a background refresh of an already-rendered list; the next tick retries.
  async refresh() {
    if (document.hidden) return

    try {
      const response = await fetch(`${this.urlValue}${window.location.search}`, { headers: { Accept: "text/html" } })
      if (response.ok) this.contentTarget.innerHTML = await response.text()
    } catch {
      // See comment above: a dropped poll isn't worth doing anything about.
    }
  }
}
